import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../utils/apiService";
import PearsonLogo from "../../../../src/images/pearson_logo.png";
import "../css/searchBox.css";

const SEARCH_API = "metasearch";
const DROPDOWN_INACTIVITY_MS = 30000;

// ---------- Helpers ----------
const getRootFromPath = (p) => {
  const parts = String(p || "").split("/").filter(Boolean);
  return parts[0] || "";
};

const extractSubpathFromRoot = (rawPath, root) => {
  let norm = String(rawPath || "")
    .replace(/\\/g, "/")
    .replace(/^[A-Za-z]:/, "");
  if (!norm.startsWith("/")) norm = "/" + norm;

  const lc = norm.toLowerCase();
  const safeRoot = String(root || "").toLowerCase().trim();

  if (safeRoot) {
    const needle = `/${safeRoot}/`;
    const i = lc.indexOf(needle);
    if (i >= 0) return norm.slice(i);
    const j = lc.lastIndexOf(needle);
    if (j >= 0) return norm.slice(j);
  }

  const htdocsNeedle = "/htdocs/";
  const k = lc.indexOf(htdocsNeedle);
  if (k >= 0) {
    const after = norm.slice(k + htdocsNeedle.length);
    return after.startsWith("/") ? after : "/" + after;
  }

  if (safeRoot) {
    const tail = norm.replace(/^\/+/, "");
    return `/${safeRoot}/${tail}`;
  }

  return norm;
};

const formatPathFromRoot = (p, root) => {
  if (!p) return "No path";
  const s = String(p).replace(/\\/g, "/");
  const safeRoot = String(root || "").toLowerCase().trim();
  if (safeRoot) {
    const idx = s.toLowerCase().indexOf(`/${safeRoot.toLowerCase()}`);
    if (idx >= 0) {
      return s.slice(idx + 1);
    }
  }
  return s.replace(/^\/+/, "");
};

const toDomId = (name = "") =>
  `file-${String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")}`;

// ---------- Component ----------
export default function MediaSearch({ currentPath }) {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [minCharWarning, setMinCharWarning] = useState(false);

  const resultsPerPage = 10;

  const containerRef = useRef(null);
  const controlsRef = useRef(null);
  const debounceTimer = useRef();
  const inactivityTimer = useRef();
  const searchRequestId = useRef(0);
  const [panelWidth, setPanelWidth] = useState(0);

  const isDropdownOpen =
    loading || noResults || (results.length > 0 && !showModal);

  const resetSearch = useCallback(() => {
    searchRequestId.current += 1;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    setQuery("");
    setResults([]);
    setLoading(false);
    setNoResults(false);
    setMinCharWarning(false);
  }, []);

  const closeDropdown = resetSearch;

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(closeDropdown, DROPDOWN_INACTIVITY_MS);
  }, [closeDropdown]);

  // Responsive width for dropdown
  useEffect(() => {
    const el = controlsRef.current;
    if (!el) return;
    const update = () =>
      setPanelWidth(Math.ceil(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, closeDropdown]);

  useEffect(() => {
    if (!isDropdownOpen) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }

    resetInactivityTimer();

    const container = containerRef.current;
    if (!container) return;

    const handleActivity = () => resetInactivityTimer();
    container.addEventListener("mousedown", handleActivity);
    container.addEventListener("keydown", handleActivity);
    container.addEventListener("input", handleActivity);

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      container.removeEventListener("mousedown", handleActivity);
      container.removeEventListener("keydown", handleActivity);
      container.removeEventListener("input", handleActivity);
    };
  }, [isDropdownOpen, closeDropdown, resetInactivityTimer]);

  const root = useMemo(() => getRootFromPath(currentPath), [currentPath]);
  const isAtMainPage = !root;

  const handleClear = () => resetSearch();

  const performSearch = async (q, kind = filterType) => {
    const requestId = ++searchRequestId.current;

    try {
      setLoading(true);
      setNoResults(false); // reset before searching
      setResults([]);

      let params = { q };

      // Map dropdown → backend params
      if (kind === "exact-file") {
        params.mode = "exact";
        params.typeFilter = "file";
      } else if (kind === "exact-folder") {
        params.mode = "exact";
        params.typeFilter = "folder";
      } else {
        params.mode = "literal";
        params.typeFilter = kind === "all" ? "all" : kind;
      }

      if (!isAtMainPage) {
        params.root = root;
        params.path = currentPath;
      }

      const res = await apiService.get(SEARCH_API, { params });
      if (requestId !== searchRequestId.current) return;

      const data = res.data || {};
      const hits = Array.isArray(data.hits) ? data.hits : [];

      setResults(hits);
      setNoResults(hits.length === 0);
    } catch (err) {
      if (requestId !== searchRequestId.current) return;
      console.error("Search failed:", err);
      setResults([]);
      setNoResults(true);
    } finally {
      if (requestId === searchRequestId.current) {
        setLoading(false);
      }
    }
  };

  // Debounce search as user types
  const onChangeQuery = (v) => {
    setQuery(v);
    const trimmed = v.trim();
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (trimmed.length === 0) {
      handleClear();
      return;
    }

    if (trimmed.length === 1) {
      setMinCharWarning(true);
      setResults([]);       
      setNoResults(false);
      setLoading(false);
      return;
    } else {
      setMinCharWarning(false);
    }

    debounceTimer.current = setTimeout(() => {
      if (trimmed.length >= 2) performSearch(trimmed, filterType);
    }, 300);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length === 1) {
      setMinCharWarning(true);
      return;
    }
    if (trimmed.length >= 2) performSearch(trimmed, filterType);
  };

  const handleItemClick = (item) => {
    setResults([]);
    setShowModal(false);
    const itemType = (item.type || "").toLowerCase();
    const isFolder = itemType === "folder";

    const itemRoot = item.root || root || getRootFromPath(item.path);
    const subpath = extractSubpathFromRoot(item.path, itemRoot);
    const appRoute = `/mdc/auth/media${subpath}`;

    if (isFolder) {
      navigate(appRoute);
      return;
    }

    const fromRoot = appRoute.replace(/^\/mdc\/auth\/media/i, "") || "/";
    const lastSlash = fromRoot.lastIndexOf("/");
    const folderPath = lastSlash > 0 ? fromRoot.slice(0, lastSlash) : "/";
    const fileName = lastSlash >= 0 ? fromRoot.slice(lastSlash + 1) : fromRoot;
    const id = toDomId(fileName);

    navigate(
      `/mdc/auth/media${folderPath}?select=${encodeURIComponent(fileName)}#${id}`,
      { state: { focus: fileName, focusId: id, focusPath: fromRoot } }
    );
  };

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(results.length / resultsPerPage));
  const indexOfLast = currentPage * resultsPerPage;
  const indexOfFirst = indexOfLast - resultsPerPage;
  const currentResults = results.slice(indexOfFirst, indexOfLast);

  const onModalpopupClose = () => setShowModal(false);

  // ---------- Render ----------
  return (
    <div
      ref={containerRef}
      className="media-search"
      style={{ "--panel-width": `${panelWidth || 0}px` }}
    >
      {/* Search controls */}
      <form ref={controlsRef} onSubmit={onSubmit} className="media-search__controls">
        <div className="media-search__input-wrapper">
          <input
            type="search"
            placeholder={isAtMainPage ? "Search files/folders..." : `Search in ${root}...`}
            value={query}
            onChange={(e) => onChangeQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && handleClear()}
            className="media-search__input"
            aria-label="Search files and folders"
          />
          {query && (
            <button
              type="button"
              className="media-search__clear"
              onClick={handleClear}
            >
              ×
            </button>
          )}
        </div>

        <select
          aria-label="Filter"
          value={filterType}
          onChange={(e) => {
            const val = e.target.value;
            setFilterType(val);
            const trimmed = query.trim();
            if (trimmed.length >= 2) performSearch(trimmed, val);
          }}
          className="media-search__select"
        >
          <option value="all">All</option>
          <option value="file">Files</option>
          <option value="folder">Folders</option>
          <option value="exact-file">Exact Files</option>
          <option value="exact-folder">Exact Folders</option>
        </select>

        <button type="submit" className="media-search__button">
          Go
        </button>
      </form>

      {/* Warning message */}
      {minCharWarning && (
        <div className="media-search__warning">
          Minimum 2 characters required for search
        </div>
      )}
      
      {loading ? (
        <div className="media-search__dropdown media-search__loading">Searching…</div>
      ) : noResults ? (
        <div className="media-search__dropdown media-search__noresults">No results found</div>
      ) : null}

      {/* Results dropdown */}
      {results.length > 0 && !showModal && (
        <div className="media-search__dropdown media-search__results">
          {results.slice(0, 10).map((item, i) => {
            const isFolder = String(item.type).toLowerCase() === "folder";
            const name =
              item.highlights?.find((h) => h.field === "name")?.snippet ||
              item.name ||
              "Unnamed";
            const pathShown = formatPathFromRoot(item.path, item.root);
            return (
              <div
                key={`${item.id || i}`}
                className="media-search__result"
                onClick={() => handleItemClick(item)}
              >
                <div className="media-search__result-row">
                  <span className="media-search__badge">
                    {isFolder ? "Folder" : "File"}
                  </span>
                  <span
                    className="media-search__name"
                    dangerouslySetInnerHTML={{ __html: name }}
                  />
                </div>
                <div className="media-search__path">{pathShown}</div>
              </div>
            );
          })}

          {results.length > 10 && (
            <button
              onClick={() => setShowModal(true)}
              className="media-search_view"
            >
              View More
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="media-search__modal-backdrop">
          <div className="media-search__modal media-search__modal--center" style={{ padding: "16px 20px" }}>
            <table border="0" width="99%" cellSpacing="3" cellPadding="0" style={{ marginBottom: "10px" }}>
              <tbody>
                <tr align="left" valign="top">
                  <td align="left" valign="middle">
                    <span className="pagetitle">&nbsp;Additional result</span>
                  </td>
                  <td align="right" valign="middle">
                    <img
                      src={PearsonLogo}
                      title="Pearson Education"
                      alt="Pearson Education"
                      width="85"
                      height="28"
                    />
                  </td>
                  <td>
                    <span
                      className="close"
                      onClick={onModalpopupClose}
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        color: "#333",
                        marginLeft: "10px",
                      }}
                    >
                      &times;
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="media-search__modal-body">
              {currentResults.map((item, i) => {
                const isFolder = String(item.type).toLowerCase() === "folder";
                const name =
                  item.highlights?.find((h) => h.field === "name")?.snippet ||
                  item.name ||
                  "Unnamed";
                const pathShown = formatPathFromRoot(item.path, item.root);
                return (
                  <div
                    key={`${item.id || i}`}
                    className="media-search__result"
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="media-search__result-row">
                      <span className="media-search__badge">
                        {isFolder ? "Folder" : "File"}
                      </span>
                      <span
                        className="media-search__name"
                        dangerouslySetInnerHTML={{ __html: name }}
                      />
                    </div>
                    <div className="media-search__path">{pathShown}</div>
                  </div>
                );
              })}
            </div>

            <div
              className="media-search__pagination"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                marginTop: "16px",
              }}
            >
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                &lt;
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
